import { API } from '@/config/api';
import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import { Package, Plus, Trash2, AlertTriangle, Archive, Calendar, DollarSign } from 'lucide-react';
import { toast } from 'sonner';


export default function Inventory() {
  const { user } = useOutletContext();
  const [vials, setVials] = useState([]);
  const [peptides, setPeptides] = useState([]);
  const [alerts, setAlerts] = useState({ low_stock: [], expired: [], expiring_soon: [] });
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [formData, setFormData] = useState({
    peptide_id: '',
    peptide_name: '',
    batch_number: '',
    purchase_date: new Date().toISOString().split('T')[0],
    expiry_date: '',
    initial_amount_mg: '',
    cost: '',
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [vialsRes, peptidesRes, alertsRes] = await Promise.all([
        fetch(`${API}/vials`, { credentials: 'include' }),
        fetch(`${API}/peptides`, { credentials: 'include' }),
        fetch(`${API}/vials/alerts`, { credentials: 'include' })
      ]);

      if (vialsRes.ok) setVials((await vialsRes.json()).filter(v => !v.archived));
      if (peptidesRes.ok) setPeptides(await peptidesRes.json());
      if (alertsRes.ok) setAlerts(await alertsRes.json());
    } catch (error) {
      console.error('Failed to fetch:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePeptideSelect = (peptideId) => {
    const peptide = peptides.find(p => p.peptide_id === peptideId);
    setFormData({
      ...formData,
      peptide_id: peptideId,
      peptide_name: peptide?.name || ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.peptide_id || !formData.initial_amount_mg) {
      toast.error('Please fill in required fields');
      return;
    }

    try {
      const response = await fetch(`${API}/vials`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...formData,
          initial_amount_mg: parseFloat(formData.initial_amount_mg),
          cost: formData.cost ? parseFloat(formData.cost) : null
        })
      });

      if (response.ok) {
        toast.success('Vial added to inventory');
        setShowDialog(false);
        setFormData({
          peptide_id: '',
          peptide_name: '',
          batch_number: '',
          purchase_date: new Date().toISOString().split('T')[0],
          expiry_date: '',
          initial_amount_mg: '',
          cost: '',
          notes: ''
        });
        fetchData();
      } else {
        toast.error('Failed to add vial');
      }
    } catch (error) {
      toast.error('Failed to add vial');
    }
  };

  const handleArchive = async (vialId) => {
    try {
      const response = await fetch(`${API}/vials/${vialId}/archive`, {
        method: 'PUT',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Vial archived');
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to archive');
    }
  };

  const handleDelete = async (vialId) => {
    if (!confirm('Delete this vial? This cannot be undone.')) return;

    try {
      const response = await fetch(`${API}/vials/${vialId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Vial deleted');
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const getUsagePercentage = (vial) => {
    return ((vial.remaining_amount_mg / vial.initial_amount_mg) * 100).toFixed(0);
  };

  const getStatusColor = (vial) => {
    const remaining = vial.remaining_amount_mg / vial.initial_amount_mg;
    if (remaining <= 0.1) return 'bg-destructive';
    if (remaining <= 0.2) return 'bg-warning';
    return 'bg-success';
  };

  const totalValue = vials.reduce((sum, v) => sum + (v.cost || 0), 0);
  const totalAlerts = alerts.low_stock.length + alerts.expired.length + alerts.expiring_soon.length;

  if (loading) {
    return (
      <div className="p-8 text-center">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4" />
        <p className="text-muted-foreground">Loading inventory...</p>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 max-w-6xl mx-auto" data-testid="inventory-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-2">Vial Inventory</h1>
          <p className="text-muted-foreground">Track your peptide vials and supplies</p>
        </div>
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogTrigger asChild>
            <Button data-testid="add-vial-button">
              <Plus className="h-4 w-4 mr-2" />
              Add Vial
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Add Vial to Inventory</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Peptide *</Label>
                <Select value={formData.peptide_id} onValueChange={handlePeptideSelect}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select peptide" />
                  </SelectTrigger>
                  <SelectContent>
                    {peptides.map(peptide => (
                      <SelectItem key={peptide.peptide_id} value={peptide.peptide_id}>
                        {peptide.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Amount (mg) *</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={formData.initial_amount_mg}
                    onChange={(e) => setFormData({...formData, initial_amount_mg: e.target.value})}
                    placeholder="5"
                    required
                  />
                </div>
                <div>
                  <Label>Cost (£)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.cost}
                    onChange={(e) => setFormData({...formData, cost: e.target.value})}
                    placeholder="45.00"
                  />
                </div>
              </div>

              <div>
                <Label>Batch Number</Label>
                <Input
                  value={formData.batch_number}
                  onChange={(e) => setFormData({...formData, batch_number: e.target.value})}
                  placeholder="LOT-123456"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Purchase Date</Label>
                  <Input
                    type="date"
                    value={formData.purchase_date}
                    onChange={(e) => setFormData({...formData, purchase_date: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Expiry Date</Label>
                  <Input
                    type="date"
                    value={formData.expiry_date}
                    onChange={(e) => setFormData({...formData, expiry_date: e.target.value})}
                  />
                </div>
              </div>

              <div>
                <Label>Notes</Label>
                <Input
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  placeholder="Supplier, storage location, etc."
                />
              </div>

              <Button type="submit" className="w-full">
                <Package className="h-4 w-4 mr-2" />
                Add to Inventory
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card className="p-4 glass-card text-center">
          <p className="text-3xl font-bold text-primary">{vials.length}</p>
          <p className="text-sm text-muted-foreground">Active Vials</p>
        </Card>
        <Card className="p-4 glass-card text-center">
          <p className="text-3xl font-bold text-success">£{totalValue.toFixed(2)}</p>
          <p className="text-sm text-muted-foreground">Total Value</p>
        </Card>
        <Card className="p-4 glass-card text-center">
          <p className="text-3xl font-bold text-info">{new Set(vials.map(v => v.peptide_name)).size}</p>
          <p className="text-sm text-muted-foreground">Peptide Types</p>
        </Card>
        <Card className={`p-4 glass-card text-center ${totalAlerts > 0 ? 'border-warning' : ''}`}>
          <p className={`text-3xl font-bold ${totalAlerts > 0 ? 'text-warning' : 'text-muted-foreground'}`}>{totalAlerts}</p>
          <p className="text-sm text-muted-foreground">Alerts</p>
        </Card>
      </div>

      {/* Alerts Section */}
      {totalAlerts > 0 && (
        <Card className="p-6 glass-card mb-6 border-warning">
          <div className="flex items-center gap-3 mb-4">
            <AlertTriangle className="h-6 w-6 text-warning" />
            <h2 className="text-xl font-bold">Inventory Alerts</h2>
          </div>
          <div className="space-y-3">
            {alerts.expired.map(vial => (
              <div key={vial.vial_id} className="flex items-center justify-between p-3 rounded-lg bg-destructive/10 border border-destructive/30">
                <div>
                  <p className="font-semibold text-destructive">{vial.peptide_name}</p>
                  <p className="text-sm text-destructive">Expired on {new Date(vial.expiry_date).toLocaleDateString()}</p>
                </div>
                <Button size="sm" variant="outline" onClick={() => handleArchive(vial.vial_id)}>
                  <Archive className="h-4 w-4 mr-1" />
                  Archive
                </Button>
              </div>
            ))}
            {alerts.expiring_soon.map(vial => (
              <div key={vial.vial_id} className="flex items-center justify-between p-3 rounded-lg bg-warning/10 border border-warning/30">
                <div>
                  <p className="font-semibold text-warning">{vial.peptide_name}</p>
                  <p className="text-sm text-warning">Expires {new Date(vial.expiry_date).toLocaleDateString()}</p>
                </div>
              </div>
            ))}
            {alerts.low_stock.map(vial => (
              <div key={vial.vial_id} className="flex items-center justify-between p-3 rounded-lg bg-orange-500/10 border border-orange-500/30">
                <div>
                  <p className="font-semibold text-orange-500">{vial.peptide_name}</p>
                  <p className="text-sm text-orange-500">Low stock: {vial.remaining_amount_mg}mg remaining</p>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Vials List */}
      {vials.length === 0 ? (
        <Card className="p-12 text-center glass-card">
          <Package className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
          <h2 className="text-xl font-semibold mb-2">No Vials in Inventory</h2>
          <p className="text-muted-foreground mb-6">
            Start tracking your peptide vials to manage your research supplies.
          </p>
          <Button onClick={() => setShowDialog(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add Your First Vial
          </Button>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {vials.map(vial => (
            <Card key={vial.vial_id} className="p-4 glass-card" data-testid={`vial-${vial.vial_id}`}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="font-semibold text-lg">{vial.peptide_name}</h3>
                  {vial.batch_number && (
                    <p className="text-xs text-muted-foreground">{vial.batch_number}</p>
                  )}
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="icon" onClick={() => handleArchive(vial.vial_id)}>
                    <Archive className="h-4 w-4 text-muted-foreground" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => handleDelete(vial.vial_id)}>
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              </div>

              <div className="mb-3">
                <div className="flex justify-between text-sm mb-1">
                  <span>Remaining</span>
                  <span className="font-medium">{vial.remaining_amount_mg}mg / {vial.initial_amount_mg}mg</span>
                </div>
                <Progress value={parseFloat(getUsagePercentage(vial))} className={`h-2 ${getStatusColor(vial)}`} />
              </div>

              <div className="grid grid-cols-2 gap-2 text-sm">
                {vial.cost && (
                  <div className="flex items-center gap-1 text-muted-foreground">
                    <DollarSign className="h-3 w-3" />
                    <span>£{vial.cost.toFixed(2)}</span>
                  </div>
                )}
                {vial.purchase_date && (
                  <div className="flex items-center gap-1 text-muted-foreground">
                    <Calendar className="h-3 w-3" />
                    <span>{new Date(vial.purchase_date).toLocaleDateString()}</span>
                  </div>
                )}
              </div>

              {vial.expiry_date && (
                <p className="text-xs text-muted-foreground mt-2">
                  Expires: {new Date(vial.expiry_date).toLocaleDateString()}
                </p>
              )}

              {vial.notes && (
                <p className="text-xs text-muted-foreground mt-2 italic">{vial.notes}</p>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
